import type { I18n } from './i18n';

export namespace I18N_CONF {
    export const COUNTRIES = ['BE', 'NL', 'LU'] as const;
    export type Country = (typeof COUNTRIES)[number];
    export const DEFAULT_COUNTRY = 'BE' satisfies Country;

    export const LANGUAGES = ['nl', 'fr', 'en'] as const;
    export type Language = (typeof LANGUAGES)[number];
    export const DEFAULT_LANGUAGE = 'nl' satisfies Language;

    export const COUNTRY_WITH_LANGUAGES = {
        BE: ['nl', 'fr', 'en'],
        NL: ['nl', 'en'],
        LU: ['fr', 'en'],
    } as const;

    export type Locale = {
        [C in Country]: `${(typeof COUNTRY_WITH_LANGUAGES)[C][number]}-${C}`;
    }[Country];
    /** List of all possible language and country combinations */
    export const LOCALES: [Locale, ...Locale[]] = Object.entries(COUNTRY_WITH_LANGUAGES).flatMap(
        ([country, languages]) => languages.map(language => `${language}-${country}` as Locale),
    ) as [Locale, ...Locale[]];

    /** A list of all of the locales that the website (and all of its portals) support.
     * Previously we supported other country codes (e.g. `NL`).
     * These are still stored in the DB but not accessible on the website. */
    export const WEBSITE_LOCALES = COUNTRY_WITH_LANGUAGES.BE.map(lang => `${lang}-BE` as const);
    export type WebsiteLocale = (typeof WEBSITE_LOCALES)[number];
    export const isWebsiteLocale = (locale: string): locale is WebsiteLocale =>
        WEBSITE_LOCALES.includes(locale as any);

    export const DEFAULT_LOCALE =
        `${DEFAULT_LANGUAGE}-${DEFAULT_COUNTRY}` as const satisfies Locale;

    export const PRODUCTION_DOMAINS = ['funkey.be', 'funkeyteambuilding.nl', 'funkey.lu'] as const;

    export const DOMAINS = [
        ...PRODUCTION_DOMAINS,

        'dev.funkey.be',

        /** For local debug only */
        'funkey.be.test',
        'funkeyteambuilding-nl.test',
        'funkey-lu.test',
        'localhost:3001',
    ] as const;
    export type Domain = (typeof DOMAINS)[number];

    export const DOMAIN_TO_COUNTRY_CODE = {
        'funkey.be': 'BE',
        'funkeyteambuilding.nl': 'NL',
        'funkey.lu': 'LU',
        'dev.funkey.be': 'BE',
        // DEV DOMAINS
        'funkey.be.test': 'BE',
        'funkeyteambuilding-nl.test': 'NL',
        'funkey-lu.test': 'LU',
        'localhost:3001': 'BE',
    } as const satisfies Record<Domain, Country>;

    export function getDomainByCountryCode(countryCode: Country): Domain;
    export function getDomainByCountryCode(_countryCode: string): string | undefined {
        return 'funkey.be' satisfies Domain;

        // Deprecated because we're only supporting BE for now

        // return Object.entries(DOMAIN_TO_COUNTRY_CODE).find(
        //     ([_, code]) => code === countryCode,
        // )?.[0];
    }

    export function getCountryForDomain(domain: Domain | (string & {})): Country {
        return domain in DOMAIN_TO_COUNTRY_CODE
            ? DOMAIN_TO_COUNTRY_CODE[domain as keyof typeof DOMAIN_TO_COUNTRY_CODE]
            : DEFAULT_COUNTRY;
    }

    export function getDefaultLanguageForDomain(domain: Domain | (string & {})): Language {
        return COUNTRIES_DEFAULT_LANGUAGE[getCountryForDomain(domain)];
    }

    export const COUNTRIES_DEFAULT_LANGUAGE = {
        BE: 'nl',
        NL: 'nl',
        LU: 'fr',
    } as const satisfies Record<Country, Language>;

    /** Names of the locale folders that contain a `messages.json` file
     *
     * @TODO(Louis): these folders aren't right and should be updated. We need to have a default for all languages and optional overrides for countries */
    export const TRANSLATED_LANGUAGES = ['en', 'fr-BE', 'nl-BE'] as const satisfies Array<
        Language | Locale
    >;
    export type TranslatedLanguage = (typeof TRANSLATED_LANGUAGES)[number];

    export const isValidLanguage = (language: string): language is I18N_CONF.Language =>
        I18N_CONF.LANGUAGES.includes(language as I18N_CONF.Language);
    export const isValidCountry = (country: string): country is I18N_CONF.Country =>
        I18N_CONF.COUNTRIES.includes(country as I18N_CONF.Country);
    export const isValidLocale = (locale: string): locale is I18N_CONF.Locale =>
        I18N_CONF.LOCALES.includes(locale as I18N_CONF.Locale);

    /**
     * Get all language and locale keys (combined without duplicates)
     * Useful for checking slugs across all translations
     */
    export const getAllTranslationKeys = (): ReadonlyArray<Language | Locale> => {
        const uniqueKeys = [
            ...I18N_CONF.LANGUAGES,
            ...I18N_CONF.LOCALES.filter(
                locale => !I18N_CONF.LANGUAGES.includes(locale.split('-')[0] as Language),
            ),
        ] as const;
        return uniqueKeys satisfies ReadonlyArray<Language | Locale>;
    };

    /** These are the paths to our internal code. Use these with `function` to dynamically translate this to the currents locale translation
     * The `default` value for now will be the `en` translations
     *
     * - `activities`: This will be used for the activities detail page (PDP). Only usable in combination with a slug.
     *      - Fe: `translateURL('activities', { slug: 'my-activity' })`
     * - `categories`: This will be used for the category landingpage. Only usable in combination with a slug.
     *      - Fe: `translateURL('categories', { slug: 'my-category' })`
     * - `cities`: This will be used for the cities page. Only usable in combination with a slug.
     *      - TODO: add translations if needed
     *      - Fe: `translateURL('cities', { slug: 'my-city' })`
     * - `teambuildings`: This will be used for the teambuilding page (PLP). Not usable with a slug.
     *      - **Slug managed in Storyblok**
     *      - Fe: `translateURL('teambuildings')`
     *
     * ### Make sure all of these (including the defaults) are in sync with the rewrites in `apps/web/next.config.js`!
     */
    export type TranslatableURL =
        | 'activities'
        | 'categories'
        | 'cities'
        | 'teambuildings'
        | 'regions'
        | 'partner'
        // System pages (FUN-5037). Not translated yet — every locale uses the same slug, so no
        // next.config.js rewrite is needed until a locale-specific slug is introduced.
        | 'cart'
        | 'checkout'
        | 'contact'
        | 'faq'
        | 'login'
        | 'forgot-password';

    /** Page types used throughout the application for navigation and routing */
    export const PAGE_TYPES = [
        'homepage',
        'activities',
        'categories',
        'regions',
        'cities',
        'partner',
        'teambuildings',
    ] as const;
    export type PageType = (typeof PAGE_TYPES)[number];
    /** These are the mappings of our internal code path to each locale that has a specific translation. There is also a default that will be used as a fallback */
    export const TRANSLATABLE_URLS = {
        activities: {
            'nl-BE': 'activiteit',
            'nl-NL': 'activiteit',
            'fr-BE': 'activite',
            'fr-LU': 'activite',
            default: 'activity',
        },
        cities: {
            'nl-BE': 'cities',
            'nl-NL': 'cities',
            'fr-BE': 'cities',
            'fr-LU': 'cities',
            default: 'cities',
        },
        categories: {
            'nl-BE': 'teambuilding',
            'nl-NL': 'teamuitje',
            'fr-BE': 'team-building',
            'fr-LU': 'team-building',
            default: 'team-building',
        },
        teambuildings: {
            'nl-BE': 'teambuildings',
            'nl-NL': 'teamuitjes',
            'fr-BE': 'teambuildings',
            'fr-LU': 'team-buildings',
            default: 'team-buildings',
        },
        regions: {
            'nl-BE': 'regions',
            'nl-NL': 'regions',
            'fr-BE': 'regions',
            'fr-LU': 'regions',
            default: 'regions',
        },
        partner: {
            'nl-BE': 'partner',
            'nl-NL': 'partner',
            'fr-BE': 'partner',
            'fr-LU': 'partner',
            default: 'partner',
        },
        cart: {
            default: 'cart',
        },
        checkout: {
            default: 'checkout',
        },
        contact: {
            default: 'contact',
        },
        faq: {
            default: 'faq',
        },
        login: {
            default: 'login',
        },
        'forgot-password': {
            default: 'forgot-password',
        },
    } as const satisfies Record<TranslatableURL, TranslatableUrlSlugs>;

    type TranslatableUrlSlugs = Partial<Record<Locale, string>> & {
        /** Should be equal as the fallback in next.config.js. All `en` locales will fallback to this */
        default: string;
    };

    /** The path prefix a locale gets on the public website.
     *
     * The country-default language is served at the bare path, every other locale keeps the
     * long form. Anything building a public link must go through this instead of hardcoding
     * `/${locale}`, or it produces the discontinued `/nl-BE/...` urls. (FUN-3315, FUN-5037)
     *
     * @example ```typescript
     * getLocalePathPrefix('nl-BE'); // => ''
     * getLocalePathPrefix('fr-BE'); // => '/fr-BE'
     * ```
     */
    export function getLocalePathPrefix(
        locale: `${Language}-${Country}`,
    ): '' | `/${Language}-${Country}` {
        const [language, country] = locale.split('-') as [Language, Country];

        return COUNTRIES_DEFAULT_LANGUAGE[country] === language ? '' : `/${locale}`;
    }

    /**
     * Helper to get the translation locale key for TRANSLATABLE_URLS lookup.
     * English locales use 'default', others use the locale as-is.
     */
    function getTranslationLocale(locale: Locale) {
        if (locale.startsWith('en-')) return 'default';
        return locale as Exclude<Locale, 'en-BE' | 'en-NL' | 'en-LU'>;
    }

    /**
     * Returns a translated url (with optional slug) for the requested url entity.
     * Setting the optional `relative` to `false` will create an absolute url. This is useful for links in emails.
     * - Example: you want to link to a activities detail page:
     * - `getTranslatedUrl('activities', {slug: 'activity-slug'})`
     * */
    export function getTranslatedUrl(
        url: I18N_CONF.TranslatableURL,
        options: {
            /** Ideally we should be able to access the locale from the current class */
            locale: I18N_CONF.Locale;
            /** @default false The returned url will be relative to the domain by default. You can override this by setting `absolute` to `true`. This will add the domain to the beginning of the url. */
            absolute?: boolean;
            slug?: string | null;
            /** @example `?foo=bar` | `foo=bar` */
            searchParams?: string;
        },
    ): string {
        const countryString = options.locale.split('-')[1] ?? 'BE';
        const country = I18N_CONF.isValidCountry(countryString) ? countryString : 'BE';

        const domain = I18N_CONF.getDomainByCountryCode(country) ?? 'funkey.be';
        const translatedURL: string[] = [
            `${(options.absolute ?? false) ? `https://${domain}` : ''}${getLocalePathPrefix(options.locale)}`,
        ];

        const slugMap: TranslatableUrlSlugs = TRANSLATABLE_URLS[url];
        translatedURL.push(slugMap[getTranslationLocale(options.locale)] ?? slugMap.default);
        if (options.slug) translatedURL.push(options.slug);

        return `${translatedURL.join('/')}${
            options.searchParams
                ? options.searchParams.startsWith('?')
                    ? options.searchParams
                    : `?${options.searchParams}`
                : ''
        }`;
    }

    /**
     * Get the translated page slug for a given page type and locale
     */
    export function getPageSlugForLocale(pageType: PageType, locale: Locale): string {
        if (pageType === 'homepage') return '';

        // Map page types to translatable URLs
        const pageTypeToTranslatableUrl: Record<Exclude<PageType, 'homepage'>, TranslatableURL> = {
            activities: 'activities',
            categories: 'categories',
            cities: 'cities',
            regions: 'regions',
            teambuildings: 'teambuildings',
            partner: 'partner',
        };

        const translatableUrl =
            pageTypeToTranslatableUrl[pageType as Exclude<PageType, 'homepage'>];
        if (!translatableUrl) return '';

        const slugMap: TranslatableUrlSlugs = TRANSLATABLE_URLS[translatableUrl];
        return slugMap[getTranslationLocale(locale)] ?? slugMap.default;
    }

    /**
     * Get all possible page slugs for a given locale
     */
    export function getPageSlugsForLocale(locale: Locale): Record<PageType, string> {
        return {
            homepage: '',
            activities: getPageSlugForLocale('activities', locale),
            categories: getPageSlugForLocale('categories', locale),
            regions: getPageSlugForLocale('regions', locale),
            cities: getPageSlugForLocale('cities', locale),
            partner: getPageSlugForLocale('partner', locale),
            teambuildings: getPageSlugForLocale('teambuildings', locale),
        };
    }
}

export class ParsedLocale {
    constructor(
        public language: I18N_CONF.Language,
        public country: I18N_CONF.Country,
    ) {}

    static fromLocale(locale: I18N_CONF.Locale) {
        const [language, country] = locale.split('-') as [
            language: I18N_CONF.Language,
            country: I18N_CONF.Country,
        ];
        return new ParsedLocale(language, country).ensureValid();
    }

    static parseUnknownLocale(unknownLocale: string) {
        const parts = unknownLocale.split('-');

        if (parts.length === 0 || parts.length > 2) return null;

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const language = parts[0]!.toLowerCase();
        const country = parts[1]?.toUpperCase();

        return {
            language: I18N_CONF.LANGUAGES.find(x => x === language) ?? null,
            country: I18N_CONF.COUNTRIES.find(x => x === country) ?? null,
        } as const;
    }

    static fromUnknownLocaleOrDefault(unknownLocale: string) {
        const parsed = ParsedLocale.parseUnknownLocale(unknownLocale);

        const country = parsed?.country ?? I18N_CONF.DEFAULT_COUNTRY;
        const language = parsed?.language ?? I18N_CONF.COUNTRIES_DEFAULT_LANGUAGE[country];

        if (I18N_CONF.isValidLocale(`${language}-${country}`)) {
            return new ParsedLocale(language, country);
        }

        /** If the country is valid, but the language is not, use the country's default language (ex: fr-LU -> nl-NL) */
        if (I18N_CONF.isValidCountry(country)) {
            return new ParsedLocale(language, country).ensureValid();
        }

        // Fallback to default locale if still invalid
        return ParsedLocale.default();
    }

    static default(): ParsedLocale {
        return new ParsedLocale(
            I18N_CONF.COUNTRIES_DEFAULT_LANGUAGE[I18N_CONF.DEFAULT_COUNTRY],
            I18N_CONF.DEFAULT_COUNTRY,
        );
    }

    /** Ensures the locale is valid for the current country.
     * If invalid, uses the country's default language. */
    ensureValid(): ParsedLocale {
        const candidateLocale = this.toString();
        if (I18N_CONF.isValidLocale(candidateLocale)) {
            return this;
        }
        return new ParsedLocale(I18N_CONF.COUNTRIES_DEFAULT_LANGUAGE[this.country], this.country);
    }

    toString(): I18N_CONF.Locale {
        return `${this.language}-${this.country}` as I18N_CONF.Locale;
    }

    toJSON(): ParsedLocale.JSON {
        return { language: this.language, country: this.country };
    }
    static fromJSON(json: ParsedLocale.JSON): ParsedLocale {
        return new ParsedLocale(json.language, json.country);
    }

    clone() {
        return new ParsedLocale(this.language, this.country);
    }
    setLanguage(language: I18N_CONF.Language) {
        this.language = language;
        return this;
    }
    setCountryCode(countryCode: I18N_CONF.Country) {
        this.country = countryCode;
        return this;
    }
}

export namespace ParsedLocale {
    export type JSON = {
        language: I18N_CONF.Language;
        country: I18N_CONF.Country;
    };
}

export const COUNTRY_CODE_TRANSLATIONS = {
    NL: i18n => i18n._('The Netherlands'),
    BE: i18n => i18n._('Belgium'),
    LU: i18n => i18n._('Luxembourg'),
    FR: i18n => i18n._('France'),
} as const satisfies Record<string, (i18n: I18n) => string>;
